import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {EditUserRequest, RegisterUserRequest, UserService} from '../../../core/services/user/user.service';
import {QueryUserInformationModel} from '../../../core/models/user/query-user.model';
import {AlertComponent} from '../../../shared/components/alert/alert.component';
import {LoadingComponent} from '../../../shared/components/loading/loading.component';
import {UserGroupService} from '../../../core/services/user/user-group.service';

import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonComponent} from '../../../shared/components/button/button.component';
import {getHttpErrorMessage} from '../../../core/helpers/http-error-response.helper';

@Component({
  selector: 'app-manage',
  imports: [
    AlertComponent,
    LoadingComponent,
    ReactiveFormsModule,
    TranslateModule,
    ButtonComponent
],
  templateUrl: './user-manage.component.html',
  styleUrl: './user-manage.component.scss'
})
export class UserManageComponent implements OnInit {
  isCreateMode = false;
  userId: number | null = null;
  loading = true;
  submitting = false;
  error: string | null = null;

  userInfo: QueryUserInformationModel | null = null;

  groups: { id: number; name: string }[] = [];
  roles: string[] = [];

  userForm!: FormGroup;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly userGroupService: UserGroupService,
    private readonly fb: FormBuilder
  ) {}

  ngOnInit(): void {
    const userIdParam = this.activatedRoute.snapshot.paramMap.get('userId');
    this.isCreateMode = !userIdParam;
    this.userId = userIdParam ? Number(userIdParam) : null;

    if (this.isCreateMode) {
      this.userForm = this.fb.group({
        email: ['', [Validators.required, Validators.email]],
        userName: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(32)]],
        userGroup: [null, [Validators.required]],
        birthday: [''],
        document: [''],
        realName: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(64)]],
        enabled: [true]
      });
    } else {
      this.userForm = this.fb.group({
        email: { value: '', disabled: true },
        userName: { value: '', disabled: true },
        userGroup: [null, [Validators.required]],
        birthday: [''],
        document: [''],
        realName: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(64)]],
        enabled: [true]
      })
    }

    this.userForm.get('userGroup')!.valueChanges.subscribe(id => this.onGroupChange(id));

    this.loadGroups();

    if (!this.isCreateMode && this.userId) {
      this.userService.getUserById(this.userId).subscribe({
        next: (result) => {
          this.userInfo = result;
          // Prefill allowed edit fields if provided by backend

          const realName = result.claims.find(c => c.type === 'name')?.value;
          const document = result.claims.find(c => c.type === 'document')?.value;
          const birthday = result.claims.find(c => c.type === 'birthdate')?.value;

          this.userForm.patchValue({
            email: result.email || '',
            userName: result.userName || '',
            realName: realName || '',
            document: document || null,
            birthday: birthday || null,
            enabled: result.enabled
          });
          // Preselect group if present
          const existingGroupId = (result.userGroups && result.userGroups.length > 0) ? result.userGroups[0].id : null;
          if (existingGroupId) {
            this.userForm.patchValue({ userGroup: existingGroupId });
            this.onGroupChange(existingGroupId);
          }
          this.loading = false;
        },
        error: err => {
          this.error = getHttpErrorMessage(err) || 'Falha ao carregar usuário.';
          this.loading = false;
        }
      });
    } else {
      this.loading = false;
    }
  }

  private loadGroups(): void {
    this.userGroupService.getUserGroups(0, 1000).subscribe({
      next: (page) => {
        this.groups = page.content
          .filter(g => g.id !== 1)
          .map(g => ({ id: g.id, name: g.name }));
      },
      error: (err) => {
        this.error = err.message || 'Falha ao carregar grupos de usuário.';
      }
    });
  }

  onGroupChange(groupId: number): void {
    if (!groupId) {
      this.roles = [];
      return;
    }

    this.userGroupService.getGroupRoles(groupId).subscribe({
      next: (roles) => this.roles = roles,
      error: (err) => {
        this.roles = [];
        console.error(err);
      }
    });
  }

  submit(): void {
    this.error = null;
    this.submitting = true;

    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      this.submitting = false;
      return;
    }

    if (this.isCreateMode) {
      const form = this.userForm.value;
      const payload: RegisterUserRequest = {
        email: form.email,
        userName: form.userName,
        userGroup: form.userGroup,
        birthday: form.birthday || null,
        document: form.document,
        realName: form.realName
      };
      this.userService.registerUser(payload).subscribe({
        next: (result: { id: number }) => {
          this.router.navigate(['/users/edit', result.id])
        },
        error: (err) => {
          this.error = getHttpErrorMessage(err) || 'Falha ao cadastrar usuário.';
          this.submitting = false;
        }
      });
    } else if (this.userId) {
      const form = this.userForm.value;
      const payload: EditUserRequest = {
        userGroup: form.userGroup,
        birthday: form.birthday,
        document: form.document,
        realName: form.realName,
        enabled: form.enabled
      };
      this.userService.editUser(this.userId, payload).subscribe({
        next: () => this.router.navigate(['/users/list']),
        error: (err) => {
          this.error = getHttpErrorMessage(err) || 'Falha ao atualizar usuário.';
          this.submitting = false;
        }
      });
    }
  }

  toNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') { return null; }
    const n = Number(value);
    return isNaN(n) ? null : n;
  }

  cancel(): void {
    this.router.navigate(['/users/list']);
  }
}
